import { ethers } from 'ethers'
import CommonERC20 from '@/connect/abi/CommonERC20'
import localeAbi from '@/connect/abi/index.js'
import http from '@/http/api'
import { GasNow } from '@/http/ws'
import BN from 'bignumber.js'

import { localCoin } from '@/connect/coin'
import { errCodeHandle, ErrorInfo } from '@/tools/error'
// import api from '@/http/api'

const coin = localCoin[process.env.VUE_APP_CHAIN]
const abi = localeAbi[process.env.VUE_APP_CHAIN]

const noDecimalBN = BN.clone({ DECIMAL_PLACES: 0 })

export class Wallet {
  constructor() {
    this._initWallet()
  }

  /**
   * 发交易
   * @param {Object} data - 交易数据
   * @return {Promise} - 交易成功或失败
   */
  sendTx(data) {
    return new Promise((resolve, reject) => {
      this._connector
        .request({
          method: 'eth_sendTransaction',
          params: [data]
        })
        .then(data => {
          resolve(data)
        })
        .catch(err => {
          if (err.code === 4001) {
            errCodeHandle.go(230001)
            reject(ErrorInfo(230001))
          } else if (err.message === 'User rejected the transaction') {
            errCodeHandle.go(230001)
            reject(ErrorInfo(230001))
          } else {
            reject(err)
          }
        })
    })
  }

  /**
   * 监听交易上链
   * @param {string} hash - 交易hash
   * @return {Promise<TransactionReceipt>} - status 为1成功 为0失败
   */
  waitForTx(hash) {
    return this._provider.waitForTransaction(hash)
  }

  /**
   * 获取交易详情
   * @param {string} hash - 交易hash
   * @return {any}
   */
  getTx(hash) {
    return this._provider.getTransaction(hash)
  }

  /**
   * 批准配额
   * @param {String} ERC20 - ERC20地址
   * @param {String} spender - 花费者地址
   * @param {String} [amount] - 要配额的金额 单位wei 默认为最大值
   * @return {Promise} 结果
   */
  approve(ERC20, spender, amount = '0x' + 'f'.repeat(64)) {
    const contract = new ethers.Contract(ERC20, CommonERC20, this._signer)
    return new Promise((resolve, reject) => {
      contract
        .approve(spender, amount)
        .then(data => {
          resolve(data.hash)
        })
        .catch(err => {
          if (err.code === 4001) {
            errCodeHandle.go(230001)
            reject(ErrorInfo(230001))
          } else if (err.message === 'User rejected the transaction') {
            errCodeHandle.go(230001)
            reject(ErrorInfo(230001))
          } else {
            reject(err)
          }
        })
    })
  }

  /**
   * 获取 批准配额 格式化数据
   * @param {String} spender - 花费者地址
   * @param {String} [amount] - 要配额的金额 单位wei 默认为最大值
   * @return {string}
   */
  getApproveData(spender, amount = '0x' + 'f'.repeat(64)) {
    const iface = new ethers.utils.Interface(abi.commonERC20)
    return iface.encodeFunctionData('approve', [spender, amount])
  }

  // /**
  //  * NPXS兑换成XS
  //  * @param {String} amount 要配额的金额 单位wei
  //  * @return {String}
  //  */
  // swap(amount) {
  //   const contract = new ethers.Contract(
  //     abi.swap.address,
  //     abi.swap.abi,
  //     this._signer
  //   )
  //   return new Promise((resolve, reject) => {
  //     contract
  //       .swap(amount)
  //       .then(data => {
  //         resolve(data)
  //       })
  //       .catch(err => {
  //         if (err.code === 4001) {
  //           errCodeHandle.go(230001)
  //           reject(ErrorInfo(230001))
  //         } else if (err.message === 'User rejected the transaction') {
  //           errCodeHandle.go(230001)
  //           reject(ErrorInfo(230001))
  //         } else {
  //           reject(err)
  //         }
  //       })
  //   })
  // }

  /**
   * 获取 swap 格式化数据
   * @param {String} amount - 兑换金额 单位wei
   * @return {string} - 格式化数据 uint256
   */
  getSwapData(amount) {
    const iface = new ethers.utils.Interface(abi.swap.abi)

    return iface.encodeFunctionData('swap', [amount])
  }

  /**
   * 获取 简易swap 格式化数据
   * @param {String} amount - 兑换金额 单位wei
   * @return {string} - 格式化数据 uint256
   */
  getSimpleSwapData(amount) {
    const iface = new ethers.utils.Interface(abi.npxs.abi)

    return iface.encodeFunctionData('transferAndCall', [
      abi.swap.address,
      amount,
      '0x00'
    ])
  }

  /**
   * 获取 Gas Price
   * @return {Promise<BigNumber>}
   */
  async getGasPrice() {
    try {
      if (process.env.VUE_APP_CHAIN !== '1') {
        throw 'no oracle'
      }
      //   const oracleGasPrice = await api.getGasPrice()
      if (this._gasNow === null) {
        this._gasNow = new GasNow()
      }
      const gasNowPrice = await this._gasNow.getGasPrice()
      const gasPrice = ethers.BigNumber.from(gasNowPrice)
      console.log('oracleGasPrice', gasNowPrice, gasPrice.toString())
      return gasPrice
    } catch {
      const rpcGasPrice = await this._provider.getGasPrice()
      // 为了统一 反回ether的bigNumber格式
      const upRpcGasPrice = ethers.BigNumber.from(
        new noDecimalBN(rpcGasPrice.toString()).multipliedBy(1.3).toString(10)
      )
      console.log('gasPrice', rpcGasPrice.toString(), upRpcGasPrice.toString())
      return upRpcGasPrice
    }
  }

  /**
   * 获取 估算gas
   * @param {Object} tx - 交易体
   * @param {string} tx.from - 发出者地址 hex
   * @param {string} tx.value - eth数量 wei hex
   * @param {string} tx.to - 接收者地址 hex
   * @param {string} tx.data - 合约数据 hex
   * @return {Promise<BigNumber>}
   */
  async estimateGas(tx) {
    // 不能传gasPrice 在mainnet链会报错
    const gasTx = {}
    if (tx.from !== undefined) {
      gasTx.from = tx.from
    }
    if (tx.value !== undefined) {
      gasTx.value = tx.value
    }
    if (tx.to !== undefined) {
      gasTx.to = tx.to
    }
    if (tx.data !== undefined) {
      gasTx.data = tx.data
    }
    const gas = await this._rpcProvider.estimateGas(gasTx)
    const upGas = ethers.BigNumber.from(
      new noDecimalBN(gas.toString()).multipliedBy(1.25).toString(10)
    )
    console.log('estimateGas', gas.toString(), upGas.toString())
    return upGas
  }

  /**
   * 获取 nonce
   * @return {Promise<number>}
   */
  getNonce() {
    return this._provider.getTransactionCount(this.account)
  }

  /**
   * NPXS兑换成XS的比例
   * @return {Promise<BigNumber>}
   */
  getSwapRate() {
    const contract = new ethers.Contract(
      abi.swap.address,
      abi.swap.abi,
      this._provider
    )
    return contract.rate()
  }

  /**
   * 获取ERC20精度
   * @param {string} ERC20 - ERC20地址
   * @return {Promise}
   */
  getDecimals(ERC20) {
    const contract = new ethers.Contract(ERC20, CommonERC20, this._provider)
    return contract.decimals()
  }

  /**
   * 查询配额
   * @param {Object[]} params - 要查询的配额数组
   * @param {string} params[].ERC20 - ERC20地址
   * @param {string} params[].spender - 花费者地址
   * @return {Promise<{}|null>}
   */
  async getAllowance(params) {
    if (params.length === 0) {
      return {}
    }
    const ERC20IdMap = {}
    const request = []
    const iface = new ethers.utils.Interface(CommonERC20)
    for (let i = 0; i < params.length; i++) {
      ERC20IdMap[i] = params[i].ERC20
      const data = iface.encodeFunctionData('allowance', [
        this.account,
        params[i].spender
      ])
      const rpcData = {
        id: i,
        jsonrpc: '2.0',
        method: 'eth_call',
        params: [{ data: data, to: params[i].ERC20 }, 'latest']
      }
      request.push(rpcData)
    }
    const res = await http.send(request)
    const ERC20Allowance = {}
    for (let item of res) {
      if (item.result !== '0x') {
        ERC20Allowance[ERC20IdMap[item.id]] = iface
          .decodeFunctionResult('allowance', item.result)
          .toString()
      } else {
        ERC20Allowance[ERC20IdMap[item.id]] = null
      }
    }
    return ERC20Allowance
  }

  /**
   * 获取当前钱包账户地址
   * @return {string | null} - 当前钱包账户地址
   */
  getAccount() {
    return this.account
  }

  /**
   * 获取余额
   * @param {undefined | String | String[]} ERC20 - ERC20地址
   * 如果是 undefined 返回 ETH 余额
   * 如果是 String 返回 ERC20 余额
   * 如果是 [String] 返回 ERC20地址为key的 余额对象
   * @return {Promise<unknown>} 余额 单位wei
   */
  getBalance(ERC20) {
    if (ERC20 === undefined) {
      return this._getETHBalance()
    } else if (typeof ERC20 === 'string') {
      return this._getERC20Balance(ERC20)
    } else if (ERC20 instanceof Array) {
      return this._getERC20Balances(ERC20)
    }
  }

  _getETHBalance() {
    return this._provider.getBalance(this.account)
  }

  /**
   * 获取ERC20币种余额
   * @param {String} ERC20 - ERC20地址
   * @return {Promise<String>} - 币种余额 单位wei
   * @private
   */
  _getERC20Balance(ERC20) {
    const contract = new ethers.Contract(ERC20, CommonERC20, this._provider)
    return contract.balanceOf(this.account)
  }

  /**
   * 获取多个ERC20币种余额
   * @param {String[]} ERC20 - ERC20地址数组
   * @return {Promise<{}|null>} - 币种余额 单位wei
   * @private
   */
  async _getERC20Balances(ERC20) {
    if (ERC20.length === 0) {
      return null
    }
    const ERC20IdMap = {}
    const request = []
    const iface = new ethers.utils.Interface(CommonERC20)
    for (let i = 0; i < ERC20.length; i++) {
      ERC20IdMap[i] = ERC20[i]
      let custom = null
      // 如果是eth
      if (ERC20[i] === coin.ETH.address) {
        custom = {
          id: i,
          jsonrpc: '2.0',
          method: 'eth_getBalance',
          params: [this.account, 'latest']
        }
      } else {
        const data = iface.encodeFunctionData('balanceOf', [this.account])
        custom = {
          id: i,
          jsonrpc: '2.0',
          method: 'eth_call',
          params: [
            {
              data: data,
              to: ERC20[i]
            },
            'latest'
          ]
        }
      }
      request.push(custom)
    }
    const res = await http.send(request)
    const ERC20Balances = {}
    for (let item of res) {
      if (item.result !== '0x') {
        if (ERC20IdMap[item.id] === coin.ETH.address) {
          ERC20Balances[ERC20IdMap[item.id]] = new BN(item.result).toString()
        } else {
          ERC20Balances[ERC20IdMap[item.id]] = iface
            .decodeFunctionResult('balanceOf', item.result)
            .toString()
        }
      } else {
        ERC20Balances[ERC20IdMap[item.id]] = null
      }
    }
    return ERC20Balances
  }

  // 获取提供者和签名者
  _getEthers() {
    this._provider = new ethers.providers.Web3Provider(this._connector)
    this._signer = this._provider.getSigner()
  }

  /**
   * 返回callback事件
   * @param {function} callback - 回调事件
   * @param {string} event - 事件名称
   * @param {string | number} [data] - 返回数据
   */
  _handleCallback(callback, event, data = '') {
    if (typeof callback === 'function') {
      callback({
        event: event,
        data: data
      })
    }
  }

  /**
   * 初始化钱包
   */
  _initWallet() {
    this.account = null
    this.chainId = null
    this._connector = null
    this._gasNow = null
    // this._provider = null
    this._provider = new ethers.providers.JsonRpcProvider(
      process.env.VUE_APP_RPC
      // process.env.VUE_APP_CHAIN
    )
    this._rpcProvider = new ethers.providers.JsonRpcProvider(
      process.env.VUE_APP_RPC
    )
    this._signer = null
  }
}
